// Infra de PDF dos formulários de apuração (Pass 1) — geração CLIENT-SIDE.
//
// Eleva o padrão do intake (apps/web-intake/src/lib/pdf-receipt.ts: layout puro
// → runs, render fino) para documentos multi-seção / multi-página. O conteúdo
// NUNCA volta ao servidor — o PDF é montado e baixado no browser do membro.
//
// Modelo de BLOCOS: cada formulário é uma função pura que devolve FormBlock[];
// `layoutForm` (pura) resolve posição/quebra/paginação em FormTextRun[];
// `renderForm` só desenha. Campo cujo valor seria CIFRADO entra como
// manualField → linha em branco rotulada "(preenchimento manual)". ZERO
// decifração neste pass.

import { jsPDF } from 'jspdf';

// ---- Geometria A4 (mm) ------------------------------------------------------
export const LEFT_MARGIN_MM = 20;
export const RIGHT_MARGIN_MM = 190;
export const TOP_MARGIN_MM = 22;
export const BOTTOM_LIMIT_MM = 282; // abaixo disto → nova página
const USABLE_WIDTH_MM = RIGHT_MARGIN_MM - LEFT_MARGIN_MM; // 170mm

const MANUAL_HINT = '(preenchimento manual)';

// ---- Modelo de blocos -------------------------------------------------------
export type FormBlock =
  | { kind: 'title'; text: string }
  | { kind: 'section'; text: string }
  | { kind: 'field'; label: string; value: string } // value '' → manual
  | { kind: 'paragraph'; text: string }
  | { kind: 'table'; headers: ReadonlyArray<string>; rows: ReadonlyArray<ReadonlyArray<string>> }
  | { kind: 'signature'; nome: string; papel: string }
  | { kind: 'spacer'; mm: number };

/** Helper: campo cujo valor seria conteúdo cifrado → fica manual neste pass. */
export function manualField(label: string): FormBlock {
  return { kind: 'field', label, value: '' };
}

/** Um run resolvido: o que desenhar, onde e em qual página. */
export interface FormTextRun {
  text: string;
  x: number;
  y: number;
  page: number;
  font: 'helvetica' | 'courier';
  style: 'normal' | 'bold' | 'italic';
  size: number;
}

function lineHeightMm(sizePt: number): number {
  return sizePt * 0.352778 * 1.25;
}

/**
 * Layout PURO. Usa `doc` apenas para medir/quebrar texto (`splitTextToSize`) —
 * não desenha nada. Devolve a lista ordenada de runs com `page` resolvido
 * (quebra quando `y` ultrapassa BOTTOM_LIMIT_MM). Verificável em teste sem
 * renderizar — paridade com computeReceiptLayout do intake.
 */
export function layoutForm(doc: jsPDF, blocks: ReadonlyArray<FormBlock>): FormTextRun[] {
  const runs: FormTextRun[] = [];
  const x = LEFT_MARGIN_MM;
  let y = TOP_MARGIN_MM;
  let page = 0;

  const ensureSpace = (needMm: number): void => {
    if (y + needMm > BOTTOM_LIMIT_MM) {
      page += 1;
      y = TOP_MARGIN_MM;
    }
  };

  const wrap = (text: string, font: FormTextRun['font'], size: number): string[] => {
    doc.setFont(font, 'normal');
    doc.setFontSize(size);
    return doc.splitTextToSize(text, USABLE_WIDTH_MM) as string[];
  };

  for (const block of blocks) {
    switch (block.kind) {
      case 'title': {
        ensureSpace(14);
        runs.push({ text: block.text, x, y, page, font: 'helvetica', style: 'bold', size: 16 });
        y += lineHeightMm(16) + 4;
        break;
      }
      case 'section': {
        ensureSpace(10);
        y += 2;
        runs.push({ text: block.text, x, y, page, font: 'helvetica', style: 'bold', size: 12 });
        y += lineHeightMm(12) + 2;
        break;
      }
      case 'field': {
        const value = block.value === '' ? MANUAL_HINT : block.value;
        const valueStyle: FormTextRun['style'] = block.value === '' ? 'italic' : 'normal';
        const lines = wrap(value, 'helvetica', 11);
        ensureSpace(lineHeightMm(11) * (lines.length + 1));
        runs.push({ text: block.label, x, y, page, font: 'helvetica', style: 'bold', size: 11 });
        y += lineHeightMm(11);
        for (const line of lines) {
          runs.push({ text: line, x, y, page, font: 'helvetica', style: valueStyle, size: 11 });
          y += lineHeightMm(11);
        }
        y += 2;
        break;
      }
      case 'paragraph': {
        const lines = wrap(block.text, 'helvetica', 10);
        ensureSpace(lineHeightMm(10) * lines.length);
        for (const line of lines) {
          runs.push({ text: line, x, y, page, font: 'helvetica', style: 'normal', size: 10 });
          y += lineHeightMm(10);
        }
        y += 2;
        break;
      }
      case 'table': {
        const colCount = block.headers.length;
        const colW = USABLE_WIDTH_MM / colCount;
        const drawRow = (cells: ReadonlyArray<string>, bold: boolean): void => {
          ensureSpace(lineHeightMm(10) + 1);
          cells.forEach((cell, i) => {
            runs.push({
              text: cell,
              x: x + i * colW,
              y,
              page,
              font: 'helvetica',
              style: bold ? 'bold' : 'normal',
              size: 9,
            });
          });
          y += lineHeightMm(10);
        };
        drawRow(block.headers, true);
        for (const row of block.rows) drawRow(row, false);
        y += 3;
        break;
      }
      case 'signature': {
        ensureSpace(20);
        y += 12;
        runs.push({
          text: '____________________________________',
          x,
          y,
          page,
          font: 'helvetica',
          style: 'normal',
          size: 11,
        });
        y += lineHeightMm(11);
        runs.push({ text: block.nome, x, y, page, font: 'helvetica', style: 'normal', size: 10 });
        y += lineHeightMm(10);
        runs.push({ text: block.papel, x, y, page, font: 'helvetica', style: 'italic', size: 9 });
        y += lineHeightMm(9) + 4;
        break;
      }
      case 'spacer': {
        y += block.mm;
        break;
      }
    }
  }

  return runs;
}

/**
 * Renderiza os blocos num Blob application/pdf. Adiciona páginas conforme o
 * maior `page` dos runs. Fino — toda a lógica de posição vive em layoutForm.
 */
export function renderForm(blocks: ReadonlyArray<FormBlock>): Blob {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const runs = layoutForm(doc, blocks);
  const maxPage = runs.reduce((m, r) => Math.max(m, r.page), 0);

  for (let p = 1; p <= maxPage; p += 1) doc.addPage();

  let current = 0;
  for (const run of runs) {
    if (run.page !== current) {
      doc.setPage(run.page + 1); // jsPDF páginas são 1-indexed
      current = run.page;
    }
    doc.setFont(run.font, run.style);
    doc.setFontSize(run.size);
    doc.text(run.text, run.x, run.y);
  }

  const blob = doc.output('blob');
  return blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
}
